import {
  DataGrid,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel,
  getGridStringOperators,
  type GridFilterOperator,
  type GridFilterItem,
  type GridSortModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid'
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Select,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { ErrorOutlineOutlined as ErrorIcon } from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import Loading from 'components/loading'
import FileUploadInput from 'components/cff/upload-boundaries/fileUploadInput'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { type AppState } from 'state/index'
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
} from 'react'
import { getClientFarm, getTranslatedName } from 'common/misc'
import colors from 'common/colors'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep, isEmpty, some } from 'es-toolkit/compat'
import { fieldsImported as resetFieldsImported } from 'actions/field'
import { createClient } from 'api/client'
import { createFarm } from 'api/farm'
import { createMultipleFields, saveMultipleFeatures } from 'api/field'
import update from 'immutability-helper'
import type { ClientModel } from 'state/client.js'
import type { FarmModel, FarmOptionModel } from 'state/farm.js'
import type { FieldModel } from 'state/field.js'
import { assignSelectedFarms } from 'actions/farm'
import { TEST_IDS } from 'test-ids'

interface FieldImportItemType {
  geomType: string
  geom_type?: string
  geo_object: string
  msg: string
  uniqueProperty: string
  farmTrxClient: ClientModel
  clientNameProperty?: string
  farmNameProperty?: string
  farmTrxFarm: FarmOptionModel
  valid: boolean | string
  fieldNamePty?: string
  attributes: { [key: string]: any }
  objects?: number
}

interface FieldImportProps {
  onImportField: (files: FileList) => void
  defaultClient?: ClientModel
  defaultFarm?: FarmOptionModel
  clients: ClientModel[]
  isTransferringData: boolean
  setTransferringData: (value: boolean) => void
  fileUpload: React.ComponentType<any>
}
interface FieldNameRecord {
  name: string
  farmId: number
}
interface FieldEntryParams {
  name: string
  geom_type?: string
  objects?: number
  farmId: number
}
interface PropertyKeyOption {
  pty: string
  key: string
  item?: string
}
const CustomBaseSelect = forwardRef((props: any, ref) => {
  const { children, ...other } = props
  return (
    <Select {...other} ref={ref}>
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          const childProps = (child as any).props || {}
          const label = childProps.children
          return cloneElement(child, {
            title:
              typeof label === 'string' || typeof label === 'number'
                ? String(label)
                : '',
            sx: {
              ...(childProps.sx || {}),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: 250,
            },
          } as any)
        }
        return child
      })}
    </Select>
  )
})

CustomBaseSelect.displayName = 'CustomBaseSelect'

export default function FieldImport(props: FieldImportProps) {
  const {
    onImportField,
    defaultClient,
    defaultFarm,
    clients,
    isTransferringData,
    setTransferringData,
  } = props

  const geojsonObject = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: null,
        id: 1,
        geometry: {
          type: 'MultiPolygon',
        },
      },
    ],
  }

  const defaultClientProperty: PropertyKeyOption = {
    pty: 'farmtrx-client',
    key: 'farmtrx-client',
  }
  const defaultFarmProperty: PropertyKeyOption = {
    pty: 'farmtrx-farm',
    key: 'farmtrx-farm',
  }
  const defaultColumnName: PropertyKeyOption = { pty: '', item: '', key: '' }

  const MINIMUM_PROPERTIES_FOR_AUTOCOMPLETE = 3 //This is to ensure that each autocomplete has enough properties to choose from

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [availableColumns, setAvailableColumns] = useState<
    { pty: string; key: string }[]
  >([])
  const defaultColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'geomType',
      headerName: t('geo_type'),
      width: 120,
      type: 'singleSelect',
      valueOptions: ['polygon', 'multipolygon'],
    },
    {
      field: 'valid',
      headerName: t('valid'),
      width: 100,
      type: 'singleSelect',
      valueOptions: ['Yes', 'No'],
    },
    {
      field: 'msg',
      headerName: t('note'),
      width: 130,
      editable: true,
    },
  ]

  const [clientProperties, setClientProperties] = useState([
    defaultClientProperty,
  ])
  const [updateBoundaries, setUpdateBoundaries] = useState(false)
  const [farmProperties, setFarmProperties] = useState([defaultFarmProperty])
  const [fieldProperties, setFieldProperties] = useState<
    { pty: string; key: string }[]
  >([])
  //Field name Autocomplete
  const [fieldNameProperty, setFieldNameProperty] = useState(defaultColumnName)
  //Farm Autocomplete
  const [farmNameProperty, setFarmNameProperty] = useState(defaultFarmProperty)
  //Client Autocomplete
  const [clientNameProperty, setClientNameProperty] = useState(
    defaultClientProperty,
  )
  const [rows, setRows] = useState<FieldImportItemType[]>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const previousSortModel = useRef<GridSortModel>([])
  const [sortedRows, setSortedRows] = useState(rows)

  const [errors, setErrors] = useState<string[]>([])

  const [selectedFields, setSelectedFields] = useState<FieldImportItemType[]>(
    [],
  )
  const selectedFieldsRef = useRef<FieldImportItemType[]>([])
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })

  const [selectedClient, setSelectedClient] = useState<ClientModel | null>(
    defaultClient || null,
  )
  const [selectedFarm, setSelectedFarm] = useState<FarmOptionModel | null>(
    defaultFarm || null,
  )

  const cffEnabled = useSelector(
    (state: AppState) => state.account.singleton.prefs?.cff?.enabled ?? false,
  )

  const fieldsImported = useSelector(
    (state: AppState) => state.field['fieldsImported'],
  )

  function getTranslatedMessage(message: string) {
    const lowercaseMessage = message.toLowerCase()
    if (lowercaseMessage == 'wrong projection. only wGS84 files are allowed.') {
      return t('error_projection')
    } else if (lowercaseMessage == 'shx or dbf file is missing.') {
      return t('error_missing_file')
    }
    return message
  }

  const handleFilesOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setTransferringData(true)
    const fileData = event.dataTransfer.files
    onImportField(fileData)
  }

  const allowDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  useEffect(() => {
    setTransferringData(false)
  }, [fieldsImported])

  useEffect(() => {
    if (cffEnabled && clients.length && defaultClient) {
      const { client, farms } = getClientFarm(
        cffEnabled,
        clients,
        `${defaultClient.id}`,
      )
      if (client) {
        setSelectedClient(client)
      } else {
        setSelectedClient(clients[0])
      }

      if (defaultFarm && farms && farms.find((f) => f.id === defaultFarm.id)) {
        setSelectedFarm(defaultFarm)
      } else if (farms && farms.length > 0) {
        // Ensure farms[0] is a FarmModel (has clientId, integrationIds)
        const farm = clients
          .flatMap((c) => c.farms)
          .find((f) => f.id === farms[0].id)
        if (farm) {
          setSelectedFarm(farm)
        } else {
          setSelectedFarm(null)
        }
      } else if (clients && clients[0].farms) {
        setSelectedFarm(clients[0].farms[0])
      }
    }
  }, [clients, cffEnabled, defaultClient, defaultFarm])

  useEffect(() => {
    if (fieldsImported && fieldsImported.properties) {
      const _clientProperties = [defaultClientProperty]
      const _farmProperties = [defaultFarmProperty]
      const _fieldProperties: any = []
      fieldsImported.properties.map((item) => {
        _fieldProperties.push({
          pty: item,
          key: item,
        })
        _clientProperties.push({ pty: item, key: item })
        _farmProperties.push({ pty: item, key: item })
      })
      setAvailableColumns(_fieldProperties)
      setClientProperties(_clientProperties)
      setFarmProperties(_farmProperties)
      setFieldProperties(_fieldProperties)
      //Both valid and invalid loop
      let final: FieldImportItemType[] = []
      Object.entries(fieldsImported && fieldsImported.fields).map((item) => {
        ;(Array.isArray(item[1]) ? item[1] : []).map(
          (innerItem: FieldImportItemType) => {
            if (innerItem.geom_type === 'POLYGON') {
              innerItem.geomType = 'polygon'
            } else if (innerItem.geom_type === 'MULTIPOLYGON') {
              innerItem.geomType = 'multipolygon'
            }
            if (innerItem.valid) {
              innerItem.valid = 'Yes'
            } else {
              innerItem.valid = 'No'
            }
            if (innerItem.msg) {
              innerItem.msg = getTranslatedMessage(innerItem.msg)
            }
            innerItem.uniqueProperty = uuidv4()
            innerItem.farmTrxClient = selectedClient ?? clients[0]
            innerItem.clientNameProperty = defaultClientProperty.pty
            innerItem.farmNameProperty = defaultFarmProperty.pty
            innerItem.farmTrxFarm =
              selectedFarm && !isEmpty(selectedFarm)
                ? selectedFarm
                : clients[0]?.farms[0]
            final = [...final, innerItem]
          },
        )
      })
      setRows(final)
    }
    //End
  }, [fieldsImported])

  useEffect(() => {
    selectedFieldsRef.current = selectedFields
  }, [selectedFields])

  useEffect(() => {
    const duplicateNames: any[] = []
    const _errors: string[] = []
    const nameCount: { [key: string]: number } = {}
    selectedFields.map((item: FieldImportItemType) => {
      const fieldNamePty = item.fieldNamePty
      if (
        item.attributes[fieldNamePty ?? ''] === '' ||
        (fieldNamePty && !item.attributes[fieldNamePty])
      ) {
        _errors.push(t('enter_selected_field_name'))
        //return
      } else {
        const name = item.attributes[fieldNamePty ?? '']

        const count = nameCount[name] ? nameCount[name] : 0
        nameCount[name] = count + 1
        if (nameCount[name] === 2) {
          duplicateNames.push(name)
        }
      }
    })
    if (duplicateNames.length) {
      let names = duplicateNames[0]
      for (let i = 1; i < duplicateNames.length; i++) {
        names += `, ${duplicateNames[i]}`
      }
      _errors.push(t('multiple_fields_same_name', { fields: names }))
    }
    setErrors(_errors)
  }, [
    selectedFields,
    fieldNameProperty,
    clientNameProperty,
    farmNameProperty,
    rows,
    updateBoundaries,
  ])

  const getFilterOperatorsForColumn = (
    options: string[],
  ): GridFilterOperator[] => {
    const MultiSelectAutocompleteFilterInput = (props: any) => {
      const { item, applyValue } = props

      const value: string[] = Array.isArray(item.value)
        ? item.value
        : item.value
          ? [item.value]
          : []

      return (
        <Autocomplete
          multiple
          limitTags={2}
          freeSolo
          options={options}
          value={value}
          onChange={(event, newValue) => {
            applyValue({ ...item, value: newValue })
          }}
          renderInput={(params) => <TextField {...params} size="small" />}
          fullWidth
        />
      )
    }

    const defaultOperators = getGridStringOperators()

    const filteredOperators = defaultOperators.filter(
      (op) => op.value === 'contains' || op.value === 'equals',
    )

    const operatorsWithMultiSelect: GridFilterOperator[] =
      filteredOperators.map((op) => ({
        ...op,
        InputComponent: MultiSelectAutocompleteFilterInput,
        getApplyFilterFn: (filterItem: GridFilterItem) => {
          const values = filterItem.value as string[] | undefined
          if (!values || values.length === 0) return null

          return (value: any) => {
            if (value === undefined || value === null) return false
            const strValue = value.toString().toLowerCase()

            if (op.value === 'contains') {
              return values.some((val) => strValue.includes(val.toLowerCase()))
            }

            if (op.value === 'equals') {
              return values.some((val) => strValue === val.toLowerCase())
            }

            return true
          }
        },
      }))

    const notEqualsOperator: GridFilterOperator = {
      label: 'is not',
      value: 'notEquals',
      InputComponent: MultiSelectAutocompleteFilterInput,
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        const values = filterItem.value as string[] | undefined
        if (!values || values.length === 0) return null

        return (value: any) => {
          if (value === undefined || value === null) return true
          const strValue = value.toString().toLowerCase()
          return values.every((val) => strValue !== val.toLowerCase())
        }
      },
    }

    return [...operatorsWithMultiSelect, notEqualsOperator]
  }

  const dynamicColumns = useMemo(() => {
    let cffCols: any[] = []
    const _columns: GridColDef[] = []
    if (fieldsImported) {
      fieldsImported.properties.map((item) => {
        const uniqueValues = Array.from(
          new Set(rows.map((row) => row.attributes[item] || '')),
        )
          .filter(Boolean)
          .sort()
        _columns.push({
          field: item,
          headerName: item,
          minWidth: 220,
          maxWidth: 300,
          flex: 1,
          type: 'string',
          filterOperators: getFilterOperatorsForColumn(uniqueValues),
          valueGetter: (value, row) => row.attributes[item] ?? '',
          renderCell: (params) => RenderImportedColumns(params),
        })
      })
      if (cffEnabled) {
        cffCols = [
          {
            field: 'client',
            headerName: t('select_client'),
            type: 'singleSelect',
            valueOptions: Array.from(new Set(clients.map((r) => r.name || '')))
              .filter(Boolean)
              .sort(),
            valueGetter: (value: any, row: any) =>
              row.farmTrxClient?.name || '',
            renderHeader: (
              props: GridRenderCellParams<any, FieldImportItemType>,
            ) => RenderClientDropdown(props, 'header', clientNameProperty),
            renderCell: (
              props: GridRenderCellParams<any, FieldImportItemType>,
            ) => RenderClientDropdown(props, 'cell', clientNameProperty),
            minWidth: 220,
            maxWidth: 300,
            flex: 1,
          },
          {
            field: 'farm',
            headerName: t('select_farm'),
            type: 'singleSelect',
            valueOptions: Array.from(
              new Set(
                clients.flatMap((c) => c.farms || []).map((f) => f.name || ''),
              ),
            )
              .filter(Boolean)
              .sort(),
            valueGetter: (value: any, row: any) => row.farmTrxFarm?.name || '',
            renderHeader: (
              props: GridRenderCellParams<any, FieldImportItemType>,
            ) => RenderFarmDropdown(props, 'header'),
            renderCell: (
              props: GridRenderCellParams<any, FieldImportItemType>,
            ) => RenderFarmDropdown(props, 'cell'),
            minWidth: 220,
            maxWidth: 300,
            flex: 1,
          },
        ]
      }
    }
    return [..._columns, ...cffCols]
  }, [
    fieldsImported,
    clientNameProperty,
    farmNameProperty,
    selectedClient,
    selectedFarm,
  ])

  const allColumns = useMemo(() => {
    return [...defaultColumns, ...dynamicColumns]
  }, [dynamicColumns])

  const handleInputChange = useCallback(
    (e: any, row: FieldImportItemType, newValue: any) => {
      let { name, value } = e.target
      const { uniqueProperty } = row
      setRows((prevRows: FieldImportItemType[]) => {
        const updatedRows = prevRows.map((row) => {
          if (row.uniqueProperty === uniqueProperty) {
            if (!name) {
              name = 'fieldNamePty'
              if (newValue && 'inputValue' in newValue) {
                value = { pty: newValue.inputValue, key: newValue.inputValue }
              } else {
                value = newValue
              }
            } else {
              value = newValue
            }
            let fields: any = selectedFieldsRef.current.map((item) => {
              if (item.uniqueProperty === uniqueProperty)
                return update(item, {
                  attributes: { $merge: { [name]: value } },
                })
              else return item
            })
            const isFieldNameEdit = name === row.fieldNamePty
            const alreadySelected = fields.some(
              (item: FieldImportItemType) =>
                item.uniqueProperty === uniqueProperty,
            )
            const canAutoSelect = cffEnabled
              ? Boolean(selectedFarm?.id) && Boolean(row.farmTrxFarm?.id)
              : true
            if (
              isFieldNameEdit &&
              (row.valid === true || row.valid === 'Yes') &&
              !alreadySelected &&
              canAutoSelect
            ) {
              fields = [
                ...fields,
                update(row, { attributes: { $merge: { [name]: value } } }),
              ]
              setRowSelectionModel((prev) => {
                if (prev.ids.has(uniqueProperty)) return prev
                const ids = new Set(prev.ids)
                ids.add(uniqueProperty)
                return { ...prev, ids }
              })
            }
            setSelectedFields(fields)
            if (row?.attributes && name in row.attributes) {
              row.attributes[name] = value
              return update(row, { attributes: { $merge: { [name]: value } } })
            } else {
              if (name === 'farmTrxClient') {
                const defaultfarm =
                  newValue['farms'].length > 0 ? newValue['farms'][0] : {}
                return update(row, {
                  $merge: {
                    [name]: value,
                    ['farmTrxFarm']: defaultfarm,
                  },
                })
              } else if (name === 'farmTrxFarm') {
                return update(row, {
                  $merge: {
                    [name]: value,
                  },
                })
              }
            }
          }
          return row
        })
        const newSortedRows = customSort(updatedRows, sortModel ?? [])
        setSortedRows(newSortedRows)

        return updatedRows
      })
    },
    [sortModel, cffEnabled, selectedFarm],
  )

  const onSetAllClients = useCallback(
    (event: any, newValue: any, reason: string) => {
      if (newValue.id !== selectedClient?.id) {
        const newFarm =
          newValue?.farms.length > 0
            ? newValue.farms[0]
            : {
                id: -1,
                name: newValue.name,
              }
        if (newValue.farms.length === 0) {
          newValue.farms.push(newFarm)
        }

        setSelectedClient(newValue)
        setSelectedFarm(newFarm)
        setRows((prevRows) => {
          return prevRows.map((row) => {
            if (row.valid) {
              row.farmTrxClient = newValue
              row.farmTrxFarm = newFarm
            }
            return row
          })
        })
      }
    },
    [rows, selectedClient],
  )

  const onSetAllFarms = useCallback(
    (event: any, newValue: any, reason: string) => {
      setSelectedFarm(newValue)
      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.valid) row.farmTrxFarm = newValue
          return row
        })
      })
    },
    [rows],
  )

  const setClientName = useCallback(
    (event: any, newValue: any, reason: string) => {
      const newFarms =
        newValue.pty === defaultClientProperty.pty ? [defaultFarmProperty] : []
      const newFields: { pty: string; key: string }[] = []
      let _farmNameProperty = farmNameProperty
      let resetFarmProperty = false
      for (let i = 0; i < availableColumns.length; i++) {
        if (
          newFarms.length === 0 &&
          farmNameProperty.pty === defaultFarmProperty.pty
        ) {
          _farmNameProperty = availableColumns[i]
        }
        if (newValue.pty !== availableColumns[i].pty) {
          newFarms.push(availableColumns[i])
        }
        if (
          availableColumns[i].pty !== _farmNameProperty.pty &&
          newValue.pty !== availableColumns[i].pty
        ) {
          newFields.push(availableColumns[i])
        }
      }
      if (_farmNameProperty.pty !== farmNameProperty.pty) {
        resetFarmProperty = true
        setFarmNameProperty(_farmNameProperty)
      }

      setFarmProperties(newFarms)
      setFarmNameProperty(newFarms[0])
      setFieldProperties(newFields)
      const value = newValue['pty']
      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (value != '' && row.valid) {
            row.clientNameProperty = value
            if (resetFarmProperty) {
              row['farmNameProperty'] = _farmNameProperty.pty
            }
          } else delete row['clientNameProperty']
          return row
        })
      })
      if (reason === 'selectOption') {
        //setClientNameProperty(newValue)
        setClientNameProperty((prev) => newValue)
      }
    },
    [rows, availableColumns, farmNameProperty, fieldNameProperty],
  )

  const onFarmChange = useCallback(
    (event: any, newValue: any, reason: string) => {
      const newClients = [defaultClientProperty]
      const newFields: { pty: string; key: string }[] = []
      for (let i = 0; i < availableColumns.length; i++) {
        if (newValue.pty !== availableColumns[i].pty) {
          newClients.push(availableColumns[i])
        }
        if (
          availableColumns[i].pty !== clientNameProperty.pty &&
          newValue.pty !== availableColumns[i].pty
        ) {
          newFields.push(availableColumns[i])
        }
      }

      setClientProperties(newClients)
      setFieldProperties(newFields)
      if (reason === 'clear') {
        rows.forEach(function (v) {
          delete v.farmNameProperty
        })
      } else {
        const value = newValue['pty']
        setRows((prevRows) => {
          return prevRows.map((row) => {
            if (value != '' && row.valid) {
              row.farmNameProperty = value
            } else delete row['farmNameProperty']
            return row
          })
        })
      }
      if (reason === 'selectOption') {
        setFarmNameProperty(newValue)
      }
    },
    [rows, availableColumns, fieldNameProperty, clientNameProperty],
  )

  const setFieldName = useCallback(
    (event: any, newValue: any, reason: string) => {
      const newClients = [defaultClientProperty]
      const newFarms =
        clientNameProperty.pty === defaultClientProperty.pty
          ? [defaultFarmProperty]
          : []
      for (let i = 0; i < availableColumns.length; i++) {
        if (
          availableColumns[i].pty !== farmNameProperty.pty &&
          newValue.pty !== availableColumns[i].pty
        ) {
          newClients.push(availableColumns[i])
        }
        if (
          availableColumns[i].pty !== clientNameProperty.pty &&
          newValue.pty !== availableColumns[i].pty
        ) {
          newFarms.push(availableColumns[i])
        }
      }

      setClientProperties(newClients)
      setFarmProperties(newFarms)
      if (reason === 'clear') {
        rows.forEach(function (v) {
          delete v.fieldNamePty
        })
      } else {
        const value = newValue['pty']
        setRows((prevRows) => {
          return prevRows.map((row) => {
            if (value != '' && row.valid) {
              row.fieldNamePty = value
            } else delete row['fieldNamePty']
            return row
          })
        })
      }
      if (reason === 'selectOption') {
        setFieldNameProperty(newValue)
      }
    },
    [rows, availableColumns, farmNameProperty, clientNameProperty],
  )

  const RenderImportedColumns = (
    params: GridRenderCellParams<any, FieldImportItemType>,
  ) => {
    const { row, field } = params
    const item = field
    return (
      <TextField
        value={row.attributes[item]}
        type="text"
        name={item}
        //fields imported properties
        disabled={
          row.valid
            ? !(
                row.fieldNamePty == item ||
                row?.farmNameProperty == item ||
                row?.clientNameProperty == item
              )
            : true
        }
        onChange={(e) => handleInputChange(e, params.row, e.target.value)}
        slotProps={{
          htmlInput: { size: 10 },
        }}
        sx={{
          verticalAlign: 'middle',
          width: '100%',
        }}
        onKeyDown={(event) => {
          event.stopPropagation()
        }}
        onKeyUp={(event) => {
          event.stopPropagation()
        }}
      />
    )
  }

  const RenderClientDropdown = (
    props: GridRenderCellParams<any, FieldImportItemType>,
    type: string,
    clientNameProperty: PropertyKeyOption,
  ) => {
    const { row } = props
    const isHeader = type === 'header'

    return (
      <Autocomplete
        disableClearable
        //on row client autocomplete
        disabled={clientNameProperty.pty !== defaultClientProperty.pty}
        value={isHeader ? selectedClient : row.farmTrxClient || null}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        style={{
          maxHeight: '40px',
          minWidth: '200px',
          flex: 1,
        }}
        sx={{
          '& input': {
            height: '1em',
          },
          '& label': {
            lineHeight: '1.5em',
            fontSize: '0.75em',
          },
        }}
        id={
          isHeader
            ? `set-all-farmtrx-clients`
            : `${row?.uniqueProperty}_clientName`
        }
        autoHighlight
        size="small"
        options={clients}
        selectOnFocus
        getOptionLabel={(option) => option.name || ''}
        renderOption={(props, option) => (
          <li {...props} title={option.name || ''}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.name}
            </div>
          </li>
        )}
        onChange={(event, newValue, reason) => {
          isHeader
            ? onSetAllClients(event, newValue, reason)
            : handleInputChange(
                { target: { name: 'farmTrxClient' } },
                row,
                newValue,
              )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('select_client')}
            label={t('farmtrx-client')}
            style={{ width: '100%', verticalAlign: 'middle' }}
          />
        )}
      />
    )
  }
  const RenderFarmDropdown = (
    props: GridRenderCellParams<any, FieldImportItemType>,
    type: string,
  ) => {
    const { row } = props
    const isHeader = type === 'header'
    return (
      <Autocomplete
        options={
          isHeader
            ? selectedClient?.farms || [{ id: 0, name: '' }]
            : row.farmTrxClient.farms
        }
        //on row farm autocomplete
        disabled={farmNameProperty.pty !== defaultFarmProperty.pty}
        value={(isHeader ? selectedFarm : row.farmTrxFarm) || null}
        style={{
          maxHeight: '40px',
          minWidth: '200px',
        }}
        sx={{
          '& input': {
            height: '1em',
          },
          '& label': {
            lineHeight: '1.5em',
            fontSize: '0.75em',
          },
        }}
        id={
          isHeader ? `set-all-farmtrx-farms` : `${row?.uniqueProperty}_farmName`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        autoHighlight
        size="small"
        selectOnFocus
        getOptionLabel={(option) => option.name || ''}
        renderOption={(props, option) => (
          <li {...props} title={option.name || ''}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.name}
            </div>
          </li>
        )}
        onChange={(event, newValue, reason) => {
          isHeader
            ? onSetAllFarms(event, newValue, reason)
            : handleInputChange(
                { target: { name: 'farmTrxFarm' } },
                row,
                newValue,
              )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('select_farm')}
            label={t('farmtrx-farm')}
            style={{ width: '100%', verticalAlign: 'middle' }}
          />
        )}
      />
    )
  }

  const closePopup = useCallback(() => {
    dispatch(resetFieldsImported(undefined))
    setFieldProperties([])
    setErrors([])
    setRows([])
    setClientNameProperty(defaultClientProperty)
    setFarmNameProperty(defaultFarmProperty)
    setFieldNameProperty(defaultColumnName)
  }, [])

  const importSelectedFields = useCallback(() => {
    const newClients: ClientModel[] = []
    const newFarms: FarmModel[] = []
    let fieldNames: FieldNameRecord[] = []

    const currentSelectedFields = sortedRows.filter((row) =>
      rowSelectionModel.ids.has(row.uniqueProperty),
    )
    if (!currentSelectedFields.length) return

    for (let i = 0; i < currentSelectedFields.length; i++) {
      const item: FieldImportItemType = currentSelectedFields[i]
      const clientName =
        clientNameProperty.pty !== defaultClientProperty.pty
          ? item.attributes[clientNameProperty.pty]
          : item.farmTrxClient?.name
      const newFarmName =
        farmNameProperty.pty !== defaultFarmProperty.pty
          ? item.attributes[farmNameProperty.pty]
          : item.farmTrxFarm?.name
      const fieldNamePty: string = item.fieldNamePty ?? ''
      const fieldName = item.attributes[fieldNamePty]
      const missingName = [
        [clientName, 'select_client'],
        [newFarmName, 'select_farm'],
        [fieldName, 'enter_selected_field_name'],
      ].find(([name]) => name == null || String(name).trim() === '')
      if (missingName) {
        setErrors([t(missingName[1])])
        return
      }
      const existingClient = clients.find(
        (client) => client.name === clientName,
      )
      if (existingClient) {
        const existingFarm = existingClient.farms.find(
          (item) => item.name === newFarmName,
        )
        if (existingFarm) {
          const fieldRecords: FieldNameRecord[] = createFieldEntry(
            {
              name: fieldName,
              geom_type: item.geom_type,
              objects: item.objects,
              farmId: existingFarm.id,
            },
            existingFarm.id,
          )
          fieldNames = fieldNames.concat(fieldRecords)
        } else {
          const farmIndex = newFarms.findIndex(
            (farmToCreate: FarmModel & { clientName?: string }) =>
              farmToCreate.name === newFarmName &&
              farmToCreate.clientName === clientName,
          )
          if (farmIndex === -1) {
            const newFarm = {
              name: newFarmName,
              clientId: existingClient.id ?? null,
              clientName: clientName,
              fields: [
                {
                  name: fieldName,
                  geom_type: item.geom_type,
                  objects: item.objects,
                },
              ],
            }
            if (existingClient) {
              newFarm.clientId = existingClient.id
            }
            //@ts-expect-error
            newFarms.push(newFarm)
          } else {
            //@ts-expect-error
            newFarms[farmIndex].fields.push({
              name: fieldName,
              geom_type: item.geom_type,
              objects: item.objects,
            })
          }
        }
      } else {
        const clientIndex = newClients.findIndex(
          (clientToCreate) => clientToCreate.name == clientName,
        )
        if (clientIndex === -1) {
          newClients.push({
            name: clientName.toString(),
            farms: [
              {
                name: newFarmName,
                // @ts-expect-error
                fields: [
                  {
                    name: fieldName,
                    geom_type: item.geom_type,
                    objects: item.objects,
                  },
                ],
              },
            ],
          })
        }
        const farmIndex = newFarms.findIndex(
          (farmToCreate) =>
            farmToCreate.name === newFarmName &&
            //@ts-expect-error
            farmToCreate.clientName === clientName,
        )

        if (farmIndex === -1) {
          newFarms.push({
            name: newFarmName,
            //@ts-expect-error
            clientName: clientName,
            fields: [
              {
                name: fieldName,
                geom_type: item.geom_type,
                objects: item.objects,
              },
            ],
          })
        } else {
          //@ts-expect-error
          newFarms[farmIndex].fields.push({
            name: fieldName,
            geom_type: item.geom_type,
            objects: item.objects,
          })
        }
      }
    }
    setTransferringData(true)
    if (newClients.length > 0) {
      Promise.all(
        newClients.map((client) =>
          dispatch(
            //@ts-expect-error
            createClient({
              name: client.name,
              streetAddress: '',
              postalCode: '',
              email: '',
            }),
          ),
        ),
      ).then((results: any[]) => {
        for (let i = 0; i < results.length; i++) {
          if (results[i].message === 'Client created') {
            const clientName = results[i].data.name
            const clientId = results[i].data.id
            for (const farm in newFarms) {
              //@ts-expect-error
              if (newFarms[farm].clientName == clientName) {
                newFarms[farm].clientId = clientId
              }
            }
          }
        }
        Promise.all(
          newFarms.map((farm) =>
            dispatch(
              //@ts-expect-error
              createFarm({
                name: farm.name,
                streetAddress: '',
                postalCode: '',
                clientId: farm.clientId,
              }),
            ),
          ),
        ).then((farmRes: any) => {
          for (let i = 0; i < farmRes.length; i++) {
            //Show them in the list of farms in the Map page, after creating as the client is automatically selected in the map page
            dispatch(assignSelectedFarms([farmRes[i].data]))
            //End
            const farmId = farmRes[i].data.id
            const clientId = farmRes[i].data.clientId
            const name = farmRes[i].data.name
            const farm = newFarms.find(
              (item) => item.clientId === clientId && item.name == name,
            )
            if (farm) {
              //@ts-expect-error
              for (let j = 0; j < farm.fields.length; j++) {
                //@ts-expect-error
                const fieldRecords = createFieldEntry(farm.fields[j], farmId)
                fieldNames = fieldNames.concat(fieldRecords)
              }
            }
          }
          if (fieldNames.length) {
            //@ts-expect-error
            dispatch(createMultipleFields(fieldNames)).then((fieldsRes) => {
              importBoundaries(fieldsRes.data) //removed fieldNames, the second parameter
            })
          }
        })
      })
    } else if (newFarms.length > 0) {
      Promise.all(
        newFarms.map((farm) =>
          dispatch(
            //@ts-expect-error
            createFarm({
              name: farm.name,
              streetAddress: '',
              postalCode: '',
              clientId: farm.clientId,
            }),
          ),
        ),
      ).then((farmRes: any[]) => {
        for (let i = 0; i < farmRes.length; i++) {
          const farmId = farmRes[i].data.id
          const clientId = farmRes[i].data.clientId
          const name = farmRes[i].data.name
          const farm = newFarms.find(
            (item) => item.clientId === clientId && item.name == name,
          )
          if (farm) {
            //@ts-expect-error
            for (let j = 0; j < farm.fields.length; j++) {
              //@ts-expect-error
              const fieldRecords = createFieldEntry(farm.fields[j], farmId)
              fieldNames = fieldNames.concat(fieldRecords)
            }
          }
        }
        if (fieldNames.length) {
          //@ts-expect-error
          dispatch(createMultipleFields(fieldNames)).then((fieldsRes) => {
            importBoundaries(fieldsRes.data) //removed fieldNames, the second parameter
          })
        }
      })
    } else {
      if (fieldNames.length) {
        //@ts-expect-error
        dispatch(createMultipleFields(fieldNames)).then((fieldsRes) => {
          importBoundaries(fieldsRes.data)
        })
      }
    }
  }, [
    sortedRows,
    rowSelectionModel,
    clients,
    clientNameProperty,
    farmNameProperty,
    updateBoundaries,
    setTransferringData,
    t,
  ])

  const createFieldEntry = function (
    item: FieldEntryParams,
    farmId: string | number,
  ) {
    const name = item.name
    const fieldNames: FieldNameRecord[] = []

    if (item.geom_type === 'POLYGON' || item.geom_type === 'MULTIPOLYGON') {
      fieldNames.push({
        name,
        farmId: typeof farmId === 'string' ? parseInt(farmId) : farmId,
      })
    }
    return fieldNames
  }

  const importBoundaries = function (fieldCollection: FieldModel[]) {
    const finalPayload: {
      id: number
      featuresObj: object
      updateExisting: boolean
    }[] = []
    const currentSelectedFields = sortedRows.filter((row) =>
      rowSelectionModel.ids.has(row.uniqueProperty),
    )
    if (currentSelectedFields.length) {
      for (let i = 0; i < currentSelectedFields.length; i++) {
        const item = currentSelectedFields[i]
        const fieldNamePty: string = item?.fieldNamePty ?? ''
        const name = item?.attributes[fieldNamePty]

        const obj = cloneDeep(geojsonObject)
        const geoObj = JSON.parse(item.geo_object).coordinates
        const id = fieldCollection.find((s) => s.name == name)?.id

        if (item.geom_type === 'POLYGON') {
          if (id) {
            // @ts-expect-error
            obj.features[0].geometry.coordinates = [geoObj]
            finalPayload.push({
              id,
              featuresObj: obj,
              updateExisting: updateBoundaries,
            })
          }
        } else if (item.geom_type === 'MULTIPOLYGON') {
          if (id) {
            // @ts-expect-error
            obj.features[0].geometry.coordinates = geoObj
            finalPayload.push({
              id,
              featuresObj: obj,
              updateExisting: updateBoundaries,
            })
          }
        }
      }
      setClientNameProperty(defaultClientProperty)
      setClientProperties([defaultClientProperty])
      setFarmNameProperty(defaultFarmProperty)
      setFarmProperties([defaultFarmProperty])
      setFieldProperties([])
      //@ts-expect-error
      dispatch(saveMultipleFeatures(finalPayload))
      dispatch(resetFieldsImported(undefined))
      setRows([])
    }
  }

  const customSort = (rows: any[], sortModel: GridSortModel) => {
    if (!sortModel || sortModel.length === 0) {
      return [...rows]
    }
    const combinedComparator = (a: any, b: any) => {
      for (const sortItem of sortModel) {
        const aValue = a.attributes[sortItem.field]
        const bValue = b.attributes[sortItem.field]
        if (aValue !== bValue) {
          if (sortItem.sort === 'asc') {
            return aValue > bValue ? 1 : -1
          } else {
            return aValue < bValue ? 1 : -1
          }
        }
      }
      return 0
    }
    return [...rows].sort(combinedComparator)
  }

  const handleSortModelChange = (model: GridSortModel, details: any) => {
    setSortModel(model)
  }

  // Update the rows here
  useEffect(() => {
    const newSortedRows = customSort(rows, sortModel ?? [])
    setSortedRows(newSortedRows)
  }, [sortModel, rows])

  const handleRowEditStart = () => {
    // Save current sort state BEFORE clearing
    previousSortModel.current = sortModel

    // Temporarily disable sorting
    setSortModel([])
  }

  const handleRowEditStop = () => {
    // Restore previous dynamic sort state
    setSortModel(previousSortModel.current)
  }

  const handleUpdateBoundaries = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUpdateBoundaries(event.target.checked)
  }

  return (
    <>
      <Box
        sx={{
          width: 1,
          flex: '1 0 0',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          border: `1px solid ${colors.brand}`,
          borderRadius: '2px',
        }}
      >
        {errors.length
          ? errors.map((e) => (
              <Box
                sx={{
                  borderLeft: `8px solid ${alpha(colors.dangerAccent, 0.42)} !important`,
                  background: `${alpha(colors.dangerAccent, 0.1)} !important`,
                  color: 'red !important',
                  fontWeight: 'bold !important',
                }}
                title={e}
                key={e}
              >
                <ErrorIcon
                  sx={{
                    color: colors.dangerAccent,
                    height: '0.7em',
                    verticalAlign: 'sub',
                  }}
                  titleAccess={t(e)}
                />
                {e}
              </Box>
            ))
          : null}
        <Box
          sx={{
            mx: 1,
            my: 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {isTransferringData ? (
            <Box
              sx={{
                width: 1,
                minWidth: 0,
                height: '100%',
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Loading $fullscreen={false} />
            </Box>
          ) : rows.length ? null : (
            <Box
              onDrop={handleFilesOnDrop}
              onDragOver={allowDrop}
              sx={{
                width: 1,
                flex: 1,
                display: 'flex',
                minWidth: 0,
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              {t('drag_drop_files')}
              <FileUploadInput
                mt="2"
                onImport={(fileList: FileList) => {
                  setTransferringData(true)
                  onImportField(fileList)
                }}
              />
            </Box>
          )}

          <Box
            style={{ width: '100%' }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pb: 1,
            }}
          >
            {fieldProperties &&
            fieldProperties.length &&
            !isTransferringData ? (
              <>
                <Box
                  sx={{
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-testid={TEST_IDS.updateBoundariesCheckbox}
                        size="small"
                        sx={{ padding: '0 4px' }}
                        onChange={handleUpdateBoundaries}
                        checked={updateBoundaries}
                      />
                    }
                    label={t('update_existing_fields')}
                  />
                  {t('update_existing_fields')}
                </Box>
                <Box
                  sx={{
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {cffEnabled ? (
                    <>
                      <Box
                        style={{ width: '20%' }}
                        sx={{
                          flexGrow: 1,
                          mr: 1,
                        }}
                      >
                        <Autocomplete
                          isOptionEqualToValue={(option, value) => {
                            return option.pty === value.pty
                          }}
                          disableClearable
                          fullWidth
                          style={{ maxHeight: '40px' }}
                          sx={{
                            '& input': {
                              height: '1em',
                            },
                            '& label': {
                              lineHeight: '1.5em',
                              fontSize: '0.75em',
                            },
                          }}
                          id="client_column"
                          autoHighlight
                          size="small"
                          options={clientProperties}
                          selectOnFocus
                          getOptionLabel={(option) =>
                            option.pty === 'farmtrx-client'
                              ? getTranslatedName(option.pty) || ''
                              : option.pty
                          }
                          value={clientNameProperty}
                          onChange={(event, newValue, reason) => {
                            setClientName(event, newValue, reason)
                          }}
                          disabled={
                            fieldsImported?.properties
                              ? fieldsImported?.properties?.length <
                                MINIMUM_PROPERTIES_FOR_AUTOCOMPLETE
                              : false
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('select_client_column')}
                              placeholder={t('clients')}
                              style={{ width: '100%' }}
                            />
                          )}
                        />
                      </Box>
                      <Box
                        style={{ width: '20%' }}
                        sx={{
                          flexGrow: 1,
                          mr: 1,
                        }}
                      >
                        <Autocomplete
                          disableClearable
                          isOptionEqualToValue={(option, value) => {
                            return option.pty === value.pty
                          }}
                          fullWidth
                          style={{ maxHeight: '40px', minWidth: '' }}
                          sx={{
                            '& input': {
                              height: '1em',
                            },
                            '& label': {
                              lineHeight: '1.5em',
                              fontSize: '0.75em',
                            },
                          }}
                          id="farm_column"
                          size="small"
                          autoHighlight
                          options={farmProperties}
                          getOptionLabel={(option) =>
                            option.pty === 'farmtrx-farm'
                              ? getTranslatedName(option.pty) || ''
                              : option.pty
                          }
                          value={farmNameProperty}
                          selectOnFocus
                          onChange={(event, newValue, reason) => {
                            onFarmChange(event, newValue, reason)
                          }}
                          disabled={
                            fieldsImported?.properties
                              ? fieldsImported?.properties?.length <
                                MINIMUM_PROPERTIES_FOR_AUTOCOMPLETE
                              : false
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('select_farm_column')}
                              style={{ width: '100%' }}
                            />
                          )}
                        />
                      </Box>
                    </>
                  ) : null}
                  <Box
                    style={{ width: '20%' }}
                    sx={{
                      flexGrow: 1,
                    }}
                  >
                    <Autocomplete
                      disableClearable
                      isOptionEqualToValue={(option, value) => {
                        return option.pty === value.pty
                      }}
                      fullWidth
                      value={fieldNameProperty}
                      style={{ maxHeight: '40px' }}
                      sx={{
                        '& input': {
                          height: '1em',
                        },
                        '& label': {
                          lineHeight: '1.5em',
                          fontSize: '0.75em',
                        },
                      }}
                      id="fieldName"
                      autoHighlight
                      size="small"
                      options={fieldProperties}
                      selectOnFocus
                      getOptionLabel={(option) => option.pty || ''}
                      onChange={(event, newValue, reason) => {
                        setFieldName(event, newValue, reason)
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('select_field_name')}
                          placeholder={t('select_field_name')}
                          style={{ width: '100%' }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </>
            ) : null}
          </Box>

          {fieldProperties && fieldProperties.length && !isTransferringData ? (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <DataGrid
                rows={sortedRows}
                columns={allColumns}
                getRowId={(row) => row.uniqueProperty}
                sx={{
                  minHeight: 0,
                  '& .MuiDataGrid-filterForm': {
                    gap: 1,
                  },
                }}
                slots={{
                  baseSelect: CustomBaseSelect,
                }}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 20]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                checkboxSelection
                disableRowSelectionOnClick
                //Only uncontrolled filtering is enabled.
                onRowSelectionModelChange={(
                  newRowSelectionModel: GridRowSelectionModel,
                ) => {
                  setRowSelectionModel(newRowSelectionModel)
                  const selectedData = sortedRows.filter((row) =>
                    newRowSelectionModel.ids.has(row.uniqueProperty),
                  )
                  setSelectedFields(selectedData)
                }}
                rowSelectionModel={rowSelectionModel}
                //Why only farm
                isRowSelectable={(params) =>
                  cffEnabled
                    ? Boolean(selectedFarm?.id) &&
                      Boolean(params?.row?.farmTrxFarm?.id) &&
                      (params?.row?.fieldNamePty ?? '').length > 0
                    : (params?.row?.fieldNamePty?.length ?? 0) > 0
                }
              />
              <Box
                sx={{
                  mx: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  sx={{ mx: 1, my: 1 }}
                  variant="contained"
                  color="primary"
                  //for import button
                  disabled={
                    cffEnabled
                      ? rowSelectionModel.ids.size === 0 ||
                        //
                        //@ts-expect-error
                        (farmNameProperty.pty === defaultFarmProperty &&
                          !selectedFarm?.id) ||
                        !some(rows, 'fieldNamePty') ||
                        errors.length > 0
                      : !selectedFields.length ||
                        !some(rows, 'fieldNamePty') ||
                        errors.length > 0
                  }
                  onClick={importSelectedFields}
                >
                  {t('import')}
                </Button>
                <Button color="secondary" onClick={closePopup}>
                  {t('cancel')}
                </Button>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  )
}
